import CryptoJS from 'crypto-js';

/*
    Client-side freshness security context (docs/freshness.md).
    Holds the per-tab session state in module memory — never written to
    localStorage or cookies so a stolen storage dump cannot yield a live seed.

    Flow:
      1. On page load, teeTimeAPI.getTeeTimeInit() receives { Freshness: { SessionID, Seed, TTL, Mode } }
         and calls initSecurityContext(freshness).
      2. Before every protected API call, teeTimeAPI spreads buildFreshHeaders() into the
         axios request headers.
      3. Every protected API response carries NextSeed; teeTimeAPI calls updateSeed(NextSeed)
         so the context is always in sync with what the server expects next.
      4. A 409 response means the seed expired or got out of sync; the caller re-invokes
         getTeeTimeInit() which triggers initSecurityContext() with a fresh context.
*/

let _ctx = { sessionID: null, seed: null, eventID: 0 };

/*
    Called once after each successful teeTimeInit response.
    Resets the event counter to 0 so every tab-session starts from EventID 1.
*/
export const initSecurityContext = (freshness) => {
    if (!freshness?.SessionID || !freshness?.Seed) return;
    _ctx = { sessionID: freshness.SessionID, seed: freshness.Seed, eventID: 0 };
};

/*
    Builds the five freshness headers for a single protected request.
    Increments EventID before computing — so EventID 1 goes on the first request,
    2 on the second, and so on. The server rejects any ID ≤ the last accepted one.

    Returns {} when no context is initialised (freshness mode is 'off' or teeTimeInit
    has not been called yet) — the request proceeds without freshness headers, which
    the server accepts in monitor/off mode.
*/
export const buildFreshHeaders = () => {
    if (!_ctx.seed) return {};

    _ctx.eventID += 1;
    const nonce     = CryptoJS.lib.WordArray.random(16).toString();   // 32 hex chars = 16 bytes
    const timestamp = Date.now().toString();
    const message   = `${_ctx.sessionID}|${nonce}|${_ctx.eventID}|${timestamp}`;
    const token     = CryptoJS.HmacSHA256(message, _ctx.seed).toString();

    return {
        'x-session-id':   _ctx.sessionID,
        'x-ui-event-id':  String(_ctx.eventID),
        'x-client-nonce': nonce,
        'x-timestamp':    timestamp,
        'x-fresh-token':  token,
    };
};

/*
    Called after every successful protected API response with the NextSeed value
    the server embedded in the body. The server has already rotated its stored seed
    to this value, so the next buildFreshHeaders() call must use it.
*/
export const updateSeed = (nextSeed) => {
    if (nextSeed) _ctx.seed = nextSeed;
};

export const hasContext = () => !!_ctx.seed;
